import React, { useState } from 'react';
import { Card, Descriptions, Table, Segmented, Tag, Button, Space, Checkbox } from 'antd';
import { FilePdfOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePDF } from '../../Utils/pdfGenerator';

const scheduleColumns = [
    {
        title: 'Installment',
        dataIndex: 'installmentNo',
        key: 'installmentNo',
        width: 100,
        align: 'center',
        sorter: (a, b) => (a.installmentNo || 0) - (b.installmentNo || 0),
        defaultSortOrder: 'ascend'
    },
    {
        title: 'Payment Date',
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        align: 'center',
        sorter: (a, b) => {
            const dateA = a.dueDate ? moment(a.dueDate).valueOf() : 0;
            const dateB = b.dueDate ? moment(b.dueDate).valueOf() : 0;
            return dateA - dateB;
        },
    },
    {
        title: 'Capital Share',
        dataIndex: 'capitalShare',
        key: 'capitalShare',
        render: v => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        align: 'right'
    },
    {
        title: 'Interest',
        dataIndex: 'interest',
        key: 'interest',
        render: v => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        align: 'right'
    },
    {
        title: 'Penalty',
        dataIndex: 'penalty',
        key: 'penalty',
        render: v => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        align: 'right'
    },
    {
        title: 'Total Pay',
        dataIndex: 'totalDue',
        key: 'totalDue',
        render: v => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        align: 'right'
    },
    {
        title: 'Payment Status',
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        render: (status) => {
            let color = 'default';
            if (status === 'Recovered' || status === 'Paid') color = 'success';
            if (status === 'Overdue') color = 'error';
            return <Tag color={color}>{status || 'Pending'}</Tag>;
        },
        align: 'center',
    },
];

const paymentColumns = [
    {
        title: 'Installment',
        dataIndex: 'installmentNo',
        key: 'installmentNo',
        width: 100,
        align: 'center',
        sorter: (a, b) => (b.installmentNo || 0) - (a.installmentNo || 0),
        defaultSortOrder: 'descend'
    },
    {
        title: 'Payment Date',
        dataIndex: 'paymentDate',
        key: 'paymentDate',
        render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        align: 'center',
        sorter: (a, b) => {
            const dateA = a.paymentDate ? moment(a.paymentDate).valueOf() : 0;
            const dateB = b.paymentDate ? moment(b.paymentDate).valueOf() : 0;
            return dateB - dateA;
        },
        defaultSortOrder: 'descend'
    },
    {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        render: v => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        align: 'right'
    },
    {
        title: 'Payment Status',
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        render: (status) => (
            <Tag color={status === 'SUCCESS' || status === 'Recovered' ? 'success' : 'error'}>
                {status || 'Unknown'}
            </Tag>
        ),
        align: 'center',
    },
    { title: 'Description', dataIndex: 'description', key: 'description', align: 'left' },
];

const LoanPaymentHistoryTable = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { loanData: rawLoanData, loanNo: rawLoanNo, branchName: stateBranchName } = location.state || {};
    const loanData = rawLoanData || {};
    const loanNo = (rawLoanNo === 'undefined' || rawLoanNo === 'null') ? '' : (rawLoanNo || '');

    const [viewMode, setViewMode] = useState('Schedule');
    const [pageSize, setPageSize] = useState(15);

    const getBranchString = (val) => {
        if (!val) return null;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null' || trimmed === '[object Object]') return null;
            return trimmed;
        }
        if (typeof val === 'object') {
            return val.branchName || val.name || val.branch || val.placeName || val.location ||
                val.branchDto?.branchName || val.branchDto?.name || null;
        }
        return null;
    };

    const rawBranchName = (
        getBranchString(loanData?.branchName) ||
        getBranchString(stateBranchName) ||
        getBranchString(loanData?.branch) ||
        getBranchString(loanData?.branchDto) ||
        getBranchString(loanData?.placeName) ||
        getBranchString(localStorage.getItem("branch")) ||
        ''
    ).trim();

    const branchName = (rawBranchName === '' || rawBranchName === 'undefined' || rawBranchName === 'null') ? '-' : rawBranchName;

    const lastPaymentDate = loanData?.lastPaymentDate
        ? loanData.lastPaymentDate
        : (loanData?.payments?.length > 0
            ? loanData.payments.reduce((latest, payment) => {
                const paymentDate = payment.paymentDate ? moment(payment.paymentDate) : null;
                return paymentDate && (!latest || paymentDate.isAfter(latest)) ? paymentDate : latest;
            }, null)?.toDate()
            : null);

    // Function to handle PDF generation
    const handleGeneratePDF = () => {
        const scheduleData = (loanData?.schedule || []).map(item => ({
            ...item,
            capitalShare: item.capitalShare ? Number(item.capitalShare).toFixed(2) : '0.00',
            interest: item.interest ? Number(item.interest).toFixed(2) : '0.00',
            penalty: item.penalty ? Number(item.penalty).toFixed(2) : '0.00',
            totalDue: item.totalDue ? Number(item.totalDue).toFixed(2) : '0.00',
            dueDate: item.dueDate ? moment(item.dueDate).format('YYYY-MM-DD') : '-',
        }));

        const paymentData = (loanData?.payments || []).map(item => ({
            ...item,
            amount: item.amount ? Number(item.amount).toFixed(2) : '0.00',
            paymentDate: item.paymentDate ? moment(item.paymentDate).format('YYYY-MM-DD') : '-',
        }));

        const loanDetails = {
            customerName: loanData.customerName || '-',
            accNumber: loanData.accNumber || '-',
            branchName: branchName,
            loanType: loanData.loanTypeName || '-',
            originalAmount: loanData.originalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00',
            remainingBalance: loanData.remainingBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00',
            totalPaid: loanData.totalPaidAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00',
            loanDate: loanData.loanDate ? moment(loanData.loanDate).format('YYYY-MM-DD') : '-',
            lastPaymentDate: lastPaymentDate ? moment(lastPaymentDate).format('YYYY-MM-DD') : '-'
        };

        const pdfTitle = `Loan Inquiry Report - ${loanNo}`;
        const tables = [
            { title: 'Loan Schedule History', columns: scheduleColumns, dataSource: scheduleData },
            { title: 'Loan Payments History', columns: paymentColumns, dataSource: paymentData }
        ];

        generatePDF(pdfTitle, tables, loanDetails);
    };

    return (
        <div style={{ padding: 10 }}>
            <Card style={{ border: '1px solid #f0f0f0', borderRadius: '5px', boxShadow: '0 2px 8px rgba(51, 39, 39, 0.1)' }}>
                <div style={{ position: 'relative', marginBottom: 20 }}>
                    <Space style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
                        <Button
                            icon={<FilePdfOutlined />}
                            onClick={handleGeneratePDF}
                            type="primary"
                            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                        >
                            Download PDF
                        </Button>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </Space>
                    <h2 align="center" style={{ margin: '0 200px' }}>
                        Loan Inquiry Summary - {loanNo || '-'}
                    </h2>
                </div>

                {/* Loan Header Info */}
                <Descriptions
                    bordered
                    column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
                    size="small"
                    style={{ marginBottom: 24 }}
                >
                    <Descriptions.Item label="Customer">{loanData.customerName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Account">{loanData.accNumber || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Branch">{branchName}</Descriptions.Item>
                    <Descriptions.Item label="Loan Type">{loanData.loanTypeName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Original Amount">
                        {loanData.originalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Remaining Balance">
                        <strong style={{ color: '#cf1322' }}>
                            {loanData.remainingBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '-'}
                        </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Paid">
                        <strong style={{ color: '#389e0d' }}>
                            {loanData.totalPaidAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '-'}
                        </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Loan Date">
                        {loanData.loanDate ? moment(loanData.loanDate).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Payment Date">
                        {lastPaymentDate ? moment(lastPaymentDate).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                </Descriptions>

                {/* Toggle Selection */}
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
                    <Segmented
                        size="large"
                        options={[
                            { label: 'Loan Schedule History', value: 'Schedule' },
                            { label: 'Loan Payments History', value: 'Payments' },
                        ]}
                        value={viewMode}
                        onChange={setViewMode}
                    />
                </div>

                {/* Conditional Table */}
                <Table
                    columns={viewMode === 'Schedule' ? scheduleColumns : paymentColumns}
                    dataSource={viewMode === 'Schedule' ? (loanData?.schedule || []) : (loanData?.payments || [])}
                    pagination={{
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '30', '50', '100', '1000'],
                        onShowSizeChange: (current, size) => setPageSize(size),
                        locale: { items_per_page: '/ page' }
                    }}
                    bordered
                    size="middle"
                    rowKey={(record, index) => `${viewMode}-${index}`}
                    locale={{ emptyText: `No ${viewMode.toLowerCase()} history found` }}
                />
            </Card>
        </div>
    );
};

export default LoanPaymentHistoryTable;